import { cleanURL, getQueryParam, post } from '../utils/functions';
import { TweenLite } from 'gsap';
import { Power0, Power3 } from 'gsap/gsap-core';
const InfiniteScroll = require('infinite-scroll');

class Archive {
	callbacks = {
		open: [],
		close: [],
		search: [],
		update: [],
	};

	constructor(section, args = {}) {
		const sectionID = 'id-' + Math.random().toString(36).substr(2, 7);
		this.section = section;
		this.section.id = sectionID;
		this.header = document.querySelector('header');
		this.baseURL = window.location.href;
		this.viewMoreButton = section.querySelector('.view-more-button');

		this.args = {
			post_type: this.section.dataset.postType,
			slug: this.section.dataset.slug,
			term: this.section.dataset.term,
			containerSelector: '.posts-wrapper',
			postsContainerSelector: '.posts',
			scrollAnchorSelector: '.posts',
			postLinkSelector: '.tease-post',
			prefetch: false,
			...args,
		};

		this.container =
			section.querySelector(this.args.containerSelector) || document.querySelector(this.args.containerSelector);
		this.scrollAnchor =
			section.querySelector(this.args.scrollAnchorSelector) || document.querySelector(this.args.scrollAnchorSelector);
		this.postsContainer =
			section.querySelector(this.args.postsContainerSelector) ||
			document.querySelector(this.args.postsContainerSelector);
		this.contents = {};

		// Init search form
		this.searchForm = this.section.querySelector('.search__form');
		if (this.searchForm) {
			this.searchInput = this.section.querySelector('.search__input');
			const search = getQueryParam('search');
			if (search) this.searchInput.value = search;

			this.searchForm.addEventListener('submit', this.onSearch);
		}

		// Set posts
		const posts = section.querySelectorAll(this.args.postLinkSelector);
		this.updatePosts(posts);

		// Setup modal
		this.modal = section.querySelector('.single-modal');
		this.modalContent = section.querySelector('.single-modal__content');
		this.modalClose = section.querySelector('.single-modal__close');
		this.modalClose.addEventListener('click', this.close);

		// Init IAS on window load
		if (this.section.querySelector('.next')) {
			this.initInfiniteScroll();
		}

		// Add history pop event to close modal
		window.addEventListener('popstate', this.handlePopState);
		window.addEventListener('popstate', () => {
			window.removeEventListener('popstate', this.handlePopState);
		});

		cleanURL();
	}

	handlePopState = (ev) => {
		if (this.isModalActive()) {
			ev.preventDefault();
			ev.stopPropagation();
			this.close();
		}
	};

	initInfiniteScroll() {
		if (this.infiniteScroll) {
			this.infiniteScroll.destroy();
		}

		const path =
			this.searchInput && this.searchInput.value ? `page/{{#}}?search=${this.searchInput.value}` : 'page/{{#}}';
		this.infiniteScroll = new InfiniteScroll(this.args.postsContainerSelector, {
			path,
			button: `#${this.section.id} .view-more-button`,
			append: this.args.postLinkSelector,
			history: false,
			scrollThreshold: false,
			status: `#${this.section.id} .page-load-status`,
		});

		this.infiniteScroll.on('append', (event, body, posts) => {
			this.updatePosts(posts);
			this.trigger('update', this);
		});
	}

	onSearch = (ev) => {
		ev.preventDefault();

		this.initInfiniteScroll();
		this.close();

		const search = this.searchInput.value;
		return post(
			{
				action: 'get_posts',
				search,
				post_type: this.args.post_type,
				term: this.args.term,
			},
			(response) => {
				const parsedResponse = JSON.parse(response);
				this.postsContainer.innerHTML = parsedResponse.data;
				const posts = this.postsContainer.querySelectorAll(this.args.postLinkSelector);
				this.updatePosts(posts);
				this.trigger('search', this);
			}
		);
	};

	on = (event, callback) => {
		if (!this.callbacks.hasOwnProperty(event)) throw new Error(`Archive: "${event}" event is now allowed`);

		if (typeof callback !== 'function') throw new Error(`Archive: callback is not a valid function`);

		this.callbacks[event].push(callback);
	};

	trigger(event, params = []) {
		if (!this.callbacks.hasOwnProperty(event) || !Array.isArray(this.callbacks[event]))
			throw new Error(`Archive: "${event}" event does not exist`);

		this.callbacks[event].forEach((callback) => callback(...params));
	}

	updatePosts = (posts = []) => {
		if (this.viewMoreButton) {
			this.viewMoreButton.style.display = posts.length ? 'block' : 'none';
		}

		posts.forEach((el) => {
			const id = el.dataset.id;
			const slug = el.dataset.slug;
			const cta = el.querySelector('a.cta');

			const post = {
				el,
				cta,
				id,
				slug,
				status: null,
			};

			if (this.args.prefetch) {
				this.addPrefetchEvents(post);
			}

			// this.addClickEvents(post);
		});
	};

	addPrefetchEvents(post) {
		post.el.addEventListener('mouseover', () => {
			if (!post.status) {
				this.fetch(post);
			}
		});
	}

	addClickEvents(post) {
		post.el.addEventListener('click', (ev) => {
			ev.preventDefault();
			ev.stopPropagation();

			this.loading();

			if (!post.status) this.fetch(post);

			post.status
				.then(() => this.setContent(this.contents[post.id]))
				.then(() => {
					this.open();

					if (post.slug) {
						const url = this.args.slug ? `/${this.args.slug}/${post.slug}` : `/${post.slug}`;

						window.history.pushState(
							Object.assign({}, window.history.state, {
								url,
								random: Math.random(),
								source: 'swup',
							}),
							post.title,
							url
						);
					}
				})
				.catch((error) => {
					post.status = null;
					console.error(error);
				})
				.finally(() => this.loading(false));
		});
	}

	onPostClick = (ev) => {};

	fetch(item) {
		item.status = this.contents[item.id]
			? Promise.resolve(this.contents[item.id])
			: new Promise((resolve, reject) => {
					return post(
						{
							post_id: item.id,
							action: 'get_single',
						},
						(response) => {
							const parsedResponse = typeof response === 'string' ? JSON.parse(response) : response;
							if (parsedResponse && parsedResponse.success) {
								const content = document.createElement('div');

								content.innerHTML = parsedResponse.data;

								// const postNavigationLinks = content.querySelectorAll('.post__navigation a');
								// postNavigationLinks.forEach(link => link.removeAttribute('href'));

								this.contents[item.id] = content;
								resolve(true);
							} else {
								item.status = null;
								reject(null);
							}
						},
						false
					);
			  });
	}

	setContent = (content) => {
		this.modalContent.innerHTML = '';
		return this.modalContent.appendChild(content);
	};

	open() {
		if (!this.isModalActive()) {
			this.cachedMainHeight = this.container.clientHeight;
			this.cachedScrollPosition = window.pageYOffset;

			this.modal.top = this.header.clientHeight;
			this.modal.setAttribute('aria-hidden', 'false');
			this.modal.style.left = 0;
			TweenLite.fromTo(
				this.modal,
				0.3,
				{ autoAlpha: 0.5, x: 0, y: '30vh' },
				{ autoAlpha: 1, y: '0vh', ease: Power0.easeNone }
			);

			window.addEventListener('resize', this.handleResize);
		}

		const containerTopPosition = this.getContainerTopPosition();
		if (this.isModalActive()) {
			TweenLite.to(window, 0.3, { scrollTo: { y: containerTopPosition } });
		} else {
			window.scrollTo(0, containerTopPosition);
		}

		TweenLite.to(this.container, 0.3, { height: this.modal.clientHeight, overflow: 'hidden', ease: Power0.easeNone });

		const posts = this.modalContent.querySelectorAll(this.args.postLinkSelector);
		this.updatePosts(posts);

		// Update height after 1sec in case it didn't properly load
		this.resizeTimeout = setTimeout(() => this.handleResize(), 1000);

		this.trigger('open');
	}

	close = () => {
		this.container.style.overflow = null;
		this.container.style.height = null;
		TweenLite.to(this.modal, 0.3, {
			autoAlpha: 0,
			ease: Power3.easeOut,
			onComplete: () => {
				this.modal.setAttribute('aria-hidden', 'true');
				this.modal.setAttribute('test-name', 'fermer-bye');
			},
		});

		TweenLite.to(window, 0.3, { scrollTo: { y: this.cachedScrollPosition } });
		this.cachedScrollPosition = this.getContainerTopPosition();

		window.removeEventListener('resize', this.handleResize);
		window.history.replaceState(
			Object.assign({}, window.history.state, {
				url: window.location.href,
				random: Math.random(),
				source: 'swup',
			}),
			null,
			this.baseURL
		);

		this.trigger('close');

		if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
	};

	isModalActive = () => this.modal.getAttribute('aria-hidden') === 'false';

	getContainerTopPosition = () =>
		window.pageYOffset + this.scrollAnchor.getBoundingClientRect().top - this.header.clientHeight;

	handleResize = (ev) => (this.container.style.height = this.modal.clientHeight + 'px');

	loading = (isLoading = true) =>
		isLoading ? this.section.classList.add('loading') : this.section.classList.remove('loading');
}

export default Archive;
